import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import StatsCard from '../components/dashboard/StatsCard';

const useQuery = () => {
    return new URLSearchParams(window.location.search);
}

const DriverMonthlySales = () => {
    const { isDark } = useTheme();
    const query = useQuery();
    const driverId = query.get('driver');
    const urlMonth = query.get('month');

    const [stats, setStats] = useState(null);
    const [sales, setSales] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [filteredSales, setFilteredSales] = useState([]);
    const [filteredExpenses, setFilteredExpenses] = useState([]);
    const [months, setMonths] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (driverId) {
            fetchData();
        }
    }, [driverId]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [statsResponse, salesResponse, expensesResponse] = await Promise.all([
                fetch(`/api/v1/driver_stats/${driverId}`),
                fetch(`/api/v1/sales?driver_id=${driverId}`),
                fetch(`/api/v1/expenses?driver_id=${driverId}`)
            ]);

            const statsData = await statsResponse.json();
            const salesData = await salesResponse.json();
            const expensesData = await expensesResponse.json();

            setStats(statsData);
            setSales(salesData);
            setExpenses(expensesData);

            // Generate months list from data
            const uniqueMonths = new Set();
            const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

            const addDateToMonths = (dateStr) => {
                const date = new Date(dateStr);
                if (!isNaN(date)) {
                    const monthYear = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
                    uniqueMonths.add(monthYear);
                }
            };

            salesData.forEach(sale => addDateToMonths(sale.date_received));
            expensesData.forEach(exp => addDateToMonths(exp.period_start));

            const sortedMonths = Array.from(uniqueMonths).sort((a, b) => new Date(b) - new Date(a));
            setMonths(sortedMonths);

            const initialMonth = urlMonth
                ? `${monthNames[parseInt(urlMonth.split('-')[1]) - 1]} ${urlMonth.split('-')[0]}` // Assuming urlMonth is YYYY-MM logic? Or maybe backend filter ID?
                // View used ID, but here we can just use the string.
                // Let's default to current month if no match, or first available.
                : sortedMonths[0] || `${monthNames[new Date().getMonth()]} ${new Date().getFullYear()}`;

            // If URL had a month parameter we might need to map it, but for now let's stick to the generated list.
            setSelectedMonth(initialMonth);

        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedMonth && sales.length > 0) {
            filterData();
        }
    }, [selectedMonth, sales, expenses]);

    const filterData = () => {
        const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

        const isSameMonth = (dateStr) => {
            const date = new Date(dateStr);
            if (isNaN(date)) return false;
            const dateMonth = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
            return dateMonth === selectedMonth;
        }

        const filteredS = sales.filter(s => isSameMonth(s.date_received));
        const filteredE = expenses.filter(e => isSameMonth(e.period_start));

        setFilteredSales(filteredS);
        setFilteredExpenses(filteredE);
    };

    const styles = {
        container: {
            padding: '2rem',
            background: isDark ? '#0a0a0a' : '#f8f9fa',
            minHeight: '100vh',
            color: isDark ? '#fff' : '#1f2937'
        },
        header: {
            marginBottom: '2rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem'
        },
        title: {
            fontSize: '1.8rem',
            fontWeight: 'bold',
            marginBottom: '0.25rem'
        },
        subTitle: {
            fontSize: '1.25rem',
            color: isDark ? '#9ca3af' : '#6b7280'
        },
        controls: {
            display: 'flex',
            gap: '1rem',
            alignItems: 'center'
        },
        select: {
            padding: '0.5rem 1rem',
            borderRadius: '0.5rem',
            border: `1px solid ${isDark ? '#374151' : '#d1d5db'}`,
            background: isDark ? '#1f2937' : '#ffffff',
            color: isDark ? '#ffffff' : '#1f2937',
            fontSize: '1rem'
        },
        statsGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem'
        },
        tablesGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '2rem'
        },
        tableWrapper: {
            background: isDark ? '#1e1e1e' : '#ffffff',
            borderRadius: '1rem',
            padding: '1rem',
            boxShadow: isDark
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        },
        tableTitle: (color) => ({
            fontSize: '1.25rem',
            fontWeight: 'bold',
            marginBottom: '1rem',
            color: color
        }),
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.875rem'
        },
        th: {
            textAlign: 'left',
            padding: '0.75rem',
            borderBottom: `2px solid ${isDark ? '#333' : '#e5e7eb'}`,
            color: isDark ? '#9ca3af' : '#6b7280',
            fontWeight: 600,
            whiteSpace: 'nowrap'
        },
        td: {
            padding: '0.75rem',
            borderBottom: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
            color: isDark ? '#d1d5db' : '#374151',
            whiteSpace: 'nowrap'
        },
        loading: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            fontSize: '1.5rem'
        }
    };

    if (loading) return <div style={styles.loading}>Loading data...</div>;
    if (!stats) return <div style={styles.loading}>Driver not found</div>;

    const { driver, car } = stats;

    // Calculations
    const totalRevenue = filteredSales.reduce((sum, s) => sum + parseFloat(s.amount || 0), 0);
    const totalExpenses = filteredExpenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
    const projectedPayout = totalRevenue - totalExpenses;

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>
                        {driver.first_name} {driver.last_name}
                    </h1>
                    <div style={styles.subTitle}>
                        {car?.model} - {car?.car_number}
                    </div>
                </div>
                <div style={styles.controls}>
                    <select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        style={styles.select}
                    >
                        {months.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                </div>
            </div>

            <div style={styles.statsGrid}>
                <StatsCard
                    title="Total Revenue"
                    value={`GHS ${totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    icon="💰"
                    color="green"
                />
                <StatsCard
                    title="Total Expenses"
                    value={`GHS ${totalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    icon="💸"
                    color="red"
                />
                <StatsCard
                    title="Net Payout"
                    value={`GHS ${projectedPayout.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    icon="🧾"
                    color={projectedPayout >= 0 ? "blue" : "red"}
                />
            </div>

            <div style={styles.tablesGrid}>
                {/* Sales Table */}
                <div style={styles.tableWrapper}>
                    <h3 style={styles.tableTitle('#10b981')}>Income</h3>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>Status</th>
                                    <th style={styles.th}>Mode</th>
                                    <th style={styles.th}>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSales.map(sale => (
                                    <tr key={sale.id}>
                                        <td style={styles.td}>{sale.date_received}</td>
                                        <td style={styles.td}>{sale.status}</td>
                                        <td style={styles.td}>{sale.payment_mode}</td>
                                        <td style={styles.td}>GHS {parseFloat(sale.amount).toFixed(2)}</td>
                                    </tr>
                                ))}
                                {filteredSales.length === 0 && (
                                    <tr><td colSpan="4" style={{ ...styles.td, textAlign: 'center' }}>No sales this month</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Expenses Table */}
                <div style={styles.tableWrapper}>
                    <h3 style={styles.tableTitle('#ef4444')}>Expenses</h3>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>Type</th>
                                    <th style={styles.th}>Mode</th>
                                    <th style={styles.th}>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredExpenses.map(expense => (
                                    <tr key={expense.id}>
                                        <td style={styles.td}>{expense.period_start}</td>
                                        <td style={styles.td}>{expense.expense_type}</td>
                                        <td style={styles.td}>{expense.expense_mode}</td>
                                        <td style={styles.td}>GHS {parseFloat(expense.amount).toFixed(2)}</td>
                                    </tr>
                                ))}
                                {filteredExpenses.length === 0 && (
                                    <tr><td colSpan="4" style={{ ...styles.td, textAlign: 'center' }}>No expenses this month</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DriverMonthlySales;
